import { useEffect, useState } from "react";
import {
  ACTION_ATTRIBUTES,
  ACTION_OBJECT_KEYWORDS,
  CONDUIT_STATUS_WORDS,
  NO_ID_OBJECTS,
  PREMISE_OBJECT_KEYWORDS,
  RELOPS_LIST,
  STATUS_WORDS,
  VALID_ATTRIBUTES,
  formatClockLiteral,
  formatDateLiteral,
  parseClockLiteral,
  parseDateLiteral,
  type RuleAction,
  type RuleAttribute,
  type RuleObjectKeyword,
  type RulePremise,
  type RuleRelop,
  type RuleVariable,
} from "../engine/controlRules/controlRuleParser.ts";
import { useT, type Translate } from "../i18n/hooks.ts";
import { NumberInput } from "./fields.tsx";
import { TrashIcon } from "./TrashIcon.tsx";
import { HashIcon } from "./HashIcon.tsx";
import { LinkIcon } from "./LinkIcon.tsx";
import {
  defaultAction,
  defaultPremise,
  defaultVariableFor,
  idOptionsFor,
  type ControlRuleDraft,
  type ElementNames,
} from "./controlRuleDraft.ts";

export type { ControlRuleDraft } from "./controlRuleDraft.ts";

type ValueKind = "number" | "status" | "time" | "date";

function valueKindFor(attribute: RuleAttribute): ValueKind {
  if (attribute === "TIME" || attribute === "CLOCKTIME") return "time";
  if (attribute === "DATE") return "date";
  if (attribute === "STATUS") return "status";
  return "number";
}

/** Buffers raw text so a partial edit (e.g. still typing "12:") isn't rejected mid-keystroke; only commits once it parses. */
function LiteralTextField({
  value,
  onChange,
  format,
  parse,
  placeholder,
  ariaLabel,
}: {
  value: number;
  onChange: (n: number) => void;
  format: (n: number) => string;
  parse: (s: string) => number | null;
  placeholder: string;
  ariaLabel: string;
}) {
  const [text, setText] = useState(() => format(value));
  useEffect(() => setText(format(value)), [value, format]);
  return (
    <input
      type="text"
      value={text}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onChange={(e) => {
        setText(e.target.value);
        const parsed = parse(e.target.value);
        if (parsed !== null) onChange(parsed);
      }}
    />
  );
}

function PremiseValueEditor({
  attribute,
  value,
  onChange,
  t,
}: {
  attribute: RuleAttribute;
  value: number;
  onChange: (n: number) => void;
  t: Translate;
}) {
  const kind = valueKindFor(attribute);
  if (kind === "status") {
    return (
      <select
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={t("controlRules.statusValue")}
      >
        <option value={1}>{t("controlRules.statusOpenOn")}</option>
        <option value={0}>{t("controlRules.statusClosedOff")}</option>
      </select>
    );
  }
  if (kind === "time") {
    return (
      <LiteralTextField
        value={value}
        onChange={onChange}
        format={formatClockLiteral}
        parse={parseClockLiteral}
        placeholder="H:MM"
        ariaLabel={t("controlRules.timeValue")}
      />
    );
  }
  if (kind === "date") {
    return (
      <LiteralTextField
        value={value}
        onChange={onChange}
        format={formatDateLiteral}
        parse={parseDateLiteral}
        placeholder="MM/DD/YYYY"
        ariaLabel={t("controlRules.dateValue")}
      />
    );
  }
  return (
    <NumberInput
      step="0.01"
      value={value}
      onChange={onChange}
      aria-label={t("controlRules.comparisonValue")}
    />
  );
}

function VariablePicker({
  variable,
  onChange,
  objectKeywords,
  attributesFor,
  names,
  t,
}: {
  variable: RuleVariable;
  onChange: (v: RuleVariable) => void;
  objectKeywords: RuleObjectKeyword[];
  attributesFor: (object: RuleObjectKeyword) => RuleAttribute[];
  names: ElementNames;
  t: Translate;
}) {
  return (
    <>
      <select
        value={variable.object}
        onChange={(e) => {
          const object = e.target.value as RuleObjectKeyword;
          const attrs = attributesFor(object);
          const attribute = attrs.includes(variable.attribute) ? variable.attribute : attrs[0];
          onChange({ ...defaultVariableFor(object, attrs, names), attribute });
        }}
        aria-label={t("controlRules.objectPicker")}
      >
        {objectKeywords.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
      {!NO_ID_OBJECTS.has(variable.object) && (
        <select
          value={variable.id}
          onChange={(e) => onChange({ ...variable, id: e.target.value })}
          aria-label={t("controlRules.idPicker")}
        >
          {idOptionsFor(variable.object, names).map((id) => (
            <option key={id} value={id}>
              {id}
            </option>
          ))}
        </select>
      )}
      <select
        value={variable.attribute}
        onChange={(e) => onChange({ ...variable, attribute: e.target.value as RuleAttribute })}
        aria-label={t("controlRules.attributePicker")}
      >
        {attributesFor(variable.object).map((a) => (
          <option key={a} value={a}>
            {a}
          </option>
        ))}
      </select>
    </>
  );
}

const premiseAttributesFor = (o: RuleObjectKeyword) => Array.from(VALID_ATTRIBUTES[o] ?? []);
const actionAttributesFor = (o: RuleObjectKeyword) =>
  ACTION_ATTRIBUTES[o as keyof typeof ACTION_ATTRIBUTES] ?? [];

function PremiseRow({
  premise,
  index,
  onChange,
  onRemove,
  names,
  t,
}: {
  premise: RulePremise;
  index: number;
  onChange: (p: RulePremise) => void;
  onRemove: () => void;
  names: ElementNames;
  t: Translate;
}) {
  return (
    <div className="control-rule-row">
      {index === 0 ? (
        <span className="control-rule-row-keyword">{t("controlRules.if")}</span>
      ) : (
        <select
          value={premise.logicOp}
          onChange={(e) => onChange({ ...premise, logicOp: e.target.value as "AND" | "OR" })}
          aria-label={t("controlRules.logicOperator")}
        >
          <option value="AND">{t("controlRules.and")}</option>
          <option value="OR">{t("controlRules.or")}</option>
        </select>
      )}
      <VariablePicker
        variable={premise.lhs}
        onChange={(lhs) => onChange({ ...premise, lhs })}
        objectKeywords={PREMISE_OBJECT_KEYWORDS}
        attributesFor={premiseAttributesFor}
        names={names}
        t={t}
      />
      <select
        value={premise.relop}
        onChange={(e) => onChange({ ...premise, relop: e.target.value as RuleRelop })}
        aria-label={t("controlRules.comparisonOperator")}
      >
        {RELOPS_LIST.map((op) => (
          <option key={op} value={op}>
            {op}
          </option>
        ))}
      </select>
      <span
        className="control-rule-rhs-kind"
        role="group"
        aria-label={t("controlRules.compareToKind")}
      >
        <button
          type="button"
          className={premise.rhsVar === null ? "active" : undefined}
          aria-pressed={premise.rhsVar === null}
          onClick={() => {
            if (premise.rhsVar === null) return;
            onChange({ ...premise, rhsVar: null, rhsLiteral: 0 });
          }}
          title={t("controlRules.compareToValue")}
          aria-label={t("controlRules.rhsKindValue")}
        >
          <HashIcon />
        </button>
        <button
          type="button"
          className={premise.rhsVar !== null ? "active" : undefined}
          aria-pressed={premise.rhsVar !== null}
          onClick={() => {
            if (premise.rhsVar !== null) return;
            onChange({
              ...premise,
              rhsLiteral: null,
              rhsVar: defaultVariableFor("NODE", premiseAttributesFor("NODE"), names),
            });
          }}
          title={t("controlRules.compareToElement")}
          aria-label={t("controlRules.rhsKindElement")}
        >
          <LinkIcon />
        </button>
      </span>
      {premise.rhsVar === null ? (
        <PremiseValueEditor
          attribute={premise.lhs.attribute}
          value={premise.rhsLiteral ?? 0}
          onChange={(rhsLiteral) => onChange({ ...premise, rhsLiteral, rhsVar: null })}
          t={t}
        />
      ) : (
        <VariablePicker
          variable={premise.rhsVar}
          onChange={(rhsVar) => onChange({ ...premise, rhsVar, rhsLiteral: null })}
          objectKeywords={PREMISE_OBJECT_KEYWORDS}
          attributesFor={premiseAttributesFor}
          names={names}
          t={t}
        />
      )}
      <button
        className="button-icon button-icon-danger"
        onClick={onRemove}
        aria-label={t("controlRules.deleteCondition")}
        title={t("controlRules.deleteCondition")}
      >
        <TrashIcon />
      </button>
    </div>
  );
}

function ActionValueEditor({
  action,
  onChange,
  curveNames,
  timeSeriesNames,
  t,
}: {
  action: RuleAction;
  onChange: (a: RuleAction) => void;
  curveNames: string[];
  timeSeriesNames: string[];
  t: Translate;
}) {
  if (action.target.attribute === "STATUS") {
    const words = action.target.object === "PUMP" ? STATUS_WORDS : CONDUIT_STATUS_WORDS;
    return (
      <select
        value={action.value}
        onChange={(e) => onChange({ target: action.target, value: Number(e.target.value) })}
        aria-label={t("controlRules.statusValue")}
      >
        {Object.entries(words).map(([word, num]) => (
          <option key={word} value={num}>
            {word}
          </option>
        ))}
      </select>
    );
  }

  // SETTING
  const kind =
    action.curveName !== undefined
      ? "curve"
      : action.tseriesName !== undefined
        ? "series"
        : "number";
  return (
    <>
      <select
        value={kind}
        onChange={(e) => {
          const next = e.target.value;
          if (next === "curve")
            onChange({ target: action.target, value: 0, curveName: curveNames[0] ?? "" });
          else if (next === "series")
            onChange({ target: action.target, value: 0, tseriesName: timeSeriesNames[0] ?? "" });
          else onChange({ target: action.target, value: action.value || 0 });
        }}
        aria-label={t("controlRules.settingKind")}
      >
        <option value="number">{t("controlRules.settingNumber")}</option>
        <option value="curve">{t("controlRules.settingCurve")}</option>
        <option value="series">{t("controlRules.settingTimeSeries")}</option>
      </select>
      {kind === "number" && (
        <NumberInput
          step="0.01"
          value={action.value}
          onChange={(v) => onChange({ target: action.target, value: v })}
          aria-label={t("controlRules.settingValue")}
        />
      )}
      {kind === "curve" && (
        <select
          value={action.curveName}
          onChange={(e) => onChange({ target: action.target, value: 0, curveName: e.target.value })}
          aria-label={t("controlRules.curvePicker")}
        >
          {curveNames.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      )}
      {kind === "series" && (
        <select
          value={action.tseriesName}
          onChange={(e) =>
            onChange({ target: action.target, value: 0, tseriesName: e.target.value })
          }
          aria-label={t("controlRules.timeSeriesPicker")}
        >
          {timeSeriesNames.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      )}
    </>
  );
}

function ActionRow({
  action,
  index,
  keyword,
  onChange,
  onRemove,
  names,
  curveNames,
  timeSeriesNames,
  t,
}: {
  action: RuleAction;
  index: number;
  keyword: string;
  onChange: (a: RuleAction) => void;
  onRemove: () => void;
  names: ElementNames;
  curveNames: string[];
  timeSeriesNames: string[];
  t: Translate;
}) {
  return (
    <div className="control-rule-row">
      <span className="control-rule-row-keyword">
        {index === 0 ? keyword : t("controlRules.and")}
      </span>
      <VariablePicker
        variable={action.target}
        onChange={(target) => onChange({ target, value: 1 })}
        objectKeywords={ACTION_OBJECT_KEYWORDS}
        attributesFor={actionAttributesFor}
        names={names}
        t={t}
      />
      <ActionValueEditor
        action={action}
        onChange={onChange}
        curveNames={curveNames}
        timeSeriesNames={timeSeriesNames}
        t={t}
      />
      <button
        className="button-icon button-icon-danger"
        onClick={onRemove}
        aria-label={t("controlRules.deleteAction")}
        title={t("controlRules.deleteAction")}
      >
        <TrashIcon />
      </button>
    </div>
  );
}

/** Structured, dropdown-driven [CONTROLS] rule editor. SWMM's premises are a flat sequential AND/OR chain with no parenthesized grouping, so there's no nested-condition UI to build here. */
export function ControlRuleBuilder({
  draft,
  onChange,
  names,
  curveNames,
  timeSeriesNames,
}: {
  draft: ControlRuleDraft;
  onChange: (draft: ControlRuleDraft) => void;
  names: ElementNames;
  curveNames: string[];
  timeSeriesNames: string[];
}) {
  const t = useT();

  function updatePremise(i: number, p: RulePremise) {
    onChange({ ...draft, premises: draft.premises.map((old, j) => (j === i ? p : old)) });
  }
  function removePremise(i: number) {
    if (draft.premises.length <= 1) return;
    onChange({ ...draft, premises: draft.premises.filter((_, j) => j !== i) });
  }
  function addPremise() {
    onChange({ ...draft, premises: [...draft.premises, defaultPremise(names)] });
  }

  function updateAction(list: "thenActions" | "elseActions", i: number, a: RuleAction) {
    onChange({ ...draft, [list]: draft[list].map((old, j) => (j === i ? a : old)) });
  }
  function removeAction(list: "thenActions" | "elseActions", i: number) {
    onChange({ ...draft, [list]: draft[list].filter((_, j) => j !== i) });
  }
  function addAction(list: "thenActions" | "elseActions") {
    onChange({ ...draft, [list]: [...draft[list], defaultAction(names)] });
  }

  return (
    <div className="control-rule-builder">
      <div className="control-rule-section">
        <h4 className="named-collection-subsection-title">{t("controlRules.ifSection")}</h4>
        {draft.premises.map((p, i) => (
          <PremiseRow
            key={i}
            premise={p}
            index={i}
            onChange={(np) => updatePremise(i, np)}
            onRemove={() => removePremise(i)}
            names={names}
            t={t}
          />
        ))}
        <button className="button" onClick={addPremise}>
          {t("controlRules.addCondition")}
        </button>
      </div>

      <div className="control-rule-section">
        <h4 className="named-collection-subsection-title">{t("controlRules.thenSection")}</h4>
        {draft.thenActions.map((a, i) => (
          <ActionRow
            key={i}
            action={a}
            index={i}
            keyword={t("controlRules.then")}
            onChange={(na) => updateAction("thenActions", i, na)}
            onRemove={() => removeAction("thenActions", i)}
            names={names}
            curveNames={curveNames}
            timeSeriesNames={timeSeriesNames}
            t={t}
          />
        ))}
        <button className="button" onClick={() => addAction("thenActions")}>
          {t("controlRules.addAction")}
        </button>
      </div>

      <div className="control-rule-section">
        <h4 className="named-collection-subsection-title">{t("controlRules.elseSection")}</h4>
        {draft.elseActions.map((a, i) => (
          <ActionRow
            key={i}
            action={a}
            index={i}
            keyword={t("controlRules.else")}
            onChange={(na) => updateAction("elseActions", i, na)}
            onRemove={() => removeAction("elseActions", i)}
            names={names}
            curveNames={curveNames}
            timeSeriesNames={timeSeriesNames}
            t={t}
          />
        ))}
        <button className="button" onClick={() => addAction("elseActions")}>
          {t("controlRules.addElseAction")}
        </button>
      </div>

      <label className="options-field control-rule-priority">
        {t("controlRules.priority")}
        <NumberInput value={draft.priority} onChange={(v) => onChange({ ...draft, priority: v })} />
      </label>
    </div>
  );
}
